import * as React from "react";
import { forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { styled } from "styled-components";
import { Button, Colored, ControlButton, Divider, Icon, IconButton, Spacer, Text, colors, easeOutCirc, levels } from "@vericus/cadmus-ui";
import * as RadixDialog from "@radix-ui/react-dialog";
import { ArrowLeftIcon, CrossIcon, Icon as Icon$1 } from "@vericus/cadmus-icons";
import xss from "xss";
//#region src/internal/utils.ts
/**
* Converts a string into a slug key by lowercasing and replacing spaces with
* underscores.
*/
function toSlugKey(value) {
	return value.trim().replace(/\s+/g, "_").toLowerCase();
}
//#endregion
//#region src/internal/useApi.ts
/** encodes params into appropriate URI for API calls */
const encodeUri = (params) => Object.entries(params).map((param) => `${encodeURIComponent(param[0])}=${encodeURIComponent(param[1])}`).join("&");
/** Create callbacks for interfacing with Codex. */
function useApi(root, user, university, env, referencing) {
	const { id: userId } = user;
	return {
		getIndexArticles: useCallback(async () => {
			const props = {
				university,
				referencing
			};
			const url = `${root}/articles?` + encodeUri(props);
			return fetch(url).then((resp) => resp.json()).catch(() => []);
		}, [
			root,
			university,
			referencing
		]),
		getSearchResults: useCallback(async (query) => {
			const url = `${root}/search?` + encodeUri({
				university,
				q: query
			});
			return fetch(url).then((resp) => resp.json()).catch(() => []);
		}, [root, university]),
		postVote: useCallback((articleId, vote) => {
			const url = `${root}/articles/${articleId}/like?${encodeUri({
				userId,
				university: `${university}:${env}`,
				vote
			})}`;
			return fetch(url, { method: "POST" });
		}, [
			root,
			userId,
			university,
			env
		])
	};
}
//#endregion
//#region src/useHubspotSearch.ts
/**
* Create a callback to search HubSpot knowledge base when given a query string. The returned results are capped at 100 articles.
*
* @param portalId Hubspot Portal ID
* @return Callback which takes a search string and returns up to 100 HubpotArticles.
*/
function useHubspotSearch(portalId) {
	return useCallback(async (query) => {
		const url = new URL("https://api.hubapi.com/contentsearch/v2/search");
		url.searchParams.append("portalId", portalId);
		url.searchParams.append("pathPrefix", "students");
		url.searchParams.append("type", "KNOWLEDGE_ARTICLE");
		url.searchParams.append("length", "LONG");
		url.searchParams.append("limit", "100");
		url.searchParams.append("term", query);
		return fetch(url.toString()).then((resp) => resp.json()).then((result) => result.results).catch(() => []);
	}, [portalId]);
}
//#endregion
//#region src/ManualContext.tsx
const defaultValue = {
	isOpen: false,
	setOpen: () => null,
	page: { pageType: "home" },
	changePage: () => null,
	goBack: () => null,
	onVote: () => null,
	query: "",
	makeQuery: () => null,
	results: [],
	hsSearchResults: [],
	articles: [],
	user: {
		id: "",
		givenName: "",
		familyName: ""
	},
	presets: {
		referencing_style: "",
		referencing_overview: "",
		academic_integrity: "",
		generative_ai: "",
		quoting: "",
		paraphrasing: "",
		declaration: "",
		start: ""
	},
	referencingStyle: ""
};
const ManualContext = React.createContext(defaultValue);
/**
* Provider for setting up the manual context and caching articles.
*
* This will supply all the various manual components like the Manual itself,
* Manual widget, Manual links, and the onboarding pages.
*
* When mounted, the articles list will be fetched and stored.
*/
const ManualProvider = (props) => {
	const { user, logger, endpoint, env, tenant, portalId = "5206287" } = props;
	const [isOpen, setOpen] = React.useState(defaultValue.isOpen);
	const [articles, setArticles] = React.useState(defaultValue.articles);
	const [page, setPage] = React.useState(defaultValue.page);
	const [pageStack, setPageStack] = React.useState([defaultValue.page]);
	const [query, setQuery] = React.useState(defaultValue.query);
	const [results, setResults] = React.useState(defaultValue.results);
	const [hsSearchResults, setHsResults] = React.useState(defaultValue.hsSearchResults);
	const normalisedRefStyle = toSlugKey(props.referencingStyle);
	const { getIndexArticles, getSearchResults, postVote } = useApi(endpoint, user, tenant, env, normalisedRefStyle);
	const getHubspotResults = useHubspotSearch(portalId);
	const presets = manualPresets(normalisedRefStyle);
	React.useEffect(() => {
		getIndexArticles().then((data) => setArticles(data));
	}, [getIndexArticles, setArticles]);
	const logEvent = React.useCallback((newPage) => {
		let location;
		let url;
		switch (newPage.pageType) {
			case "article":
				location = newPage.selectedArticle.slug;
				url = newPage.selectedArticle.url;
				break;
			case "index":
				location = newPage.categoryName;
				break;
			case "result": location = query;
		}
		logger({
			type: "MANUAL_VIEW",
			page: newPage.pageType,
			location,
			url
		});
	}, [logger, query]);
	const changePage = React.useCallback((newPage) => {
		if (newPage.pageType === "article" && newPage.selectedArticle.url != null && newPage.selectedArticle.url !== "") window.open(newPage.selectedArticle.url, "_blank");
		else {
			setPage(newPage);
			switch (newPage.pageType) {
				case "home":
					setPageStack([newPage]);
					break;
				case "result":
					if (!(pageStack[pageStack.length - 1].pageType === "result")) setPageStack([...pageStack, newPage]);
					break;
				default: setPageStack([...pageStack, newPage]);
			}
		}
		logEvent(newPage);
	}, [pageStack, logEvent]);
	const goBack = React.useCallback(() => {
		const newStack = pageStack.slice(0, pageStack.length - 1);
		setPageStack(newStack.length === 0 ? [{ pageType: "home" }] : newStack);
		const newPage = newStack[newStack.length - 1];
		setPage(newPage != null ? newPage : { pageType: "home" });
		logEvent(newPage != null ? newPage : { pageType: "home" });
	}, [pageStack, logEvent]);
	/** value to be provided */
	const value = {
		user,
		isOpen,
		setOpen,
		page,
		changePage,
		goBack,
		query,
		makeQuery: React.useCallback(async (query) => {
			setQuery(query);
			if (query === "") goBack();
			else {
				const [articles, hubSpotArticles] = await Promise.all([getSearchResults(query), getHubspotResults(query)]);
				if (articles != null) setResults(articles);
				setHsResults(hubSpotArticles);
				changePage({ pageType: "result" });
			}
		}, [
			getSearchResults,
			goBack,
			changePage,
			getHubspotResults
		]),
		results,
		hsSearchResults,
		onVote: React.useCallback((vote, article) => {
			const updatedArticles = articles.map((a) => a.id === article.id ? {
				...a,
				vote
			} : a);
			setArticles(updatedArticles);
			if (vote !== "") postVote(article.id, vote);
		}, [articles, postVote]),
		articles,
		presets,
		referencingStyle: normalisedRefStyle
	};
	return /* @__PURE__ */ jsx(ManualContext.Provider, {
		value,
		children: props.children
	});
};
//#endregion
//#region src/manual-presets.ts
/**
* Create a mapping of preset string keys to Article slugs.
*
* These slugs might be different depending on the passed
* arguments, but the preset keys will remain the same.
*/
function manualPresets(referencingStyle) {
	const style = toSlugKey(referencingStyle);
	const hasReferencingStyle = style !== "" && style !== "no_referencing";
	const stylePath = hasReferencingStyle ? `/${style}/` : "/no_referencing/";
	const referencingList = `referencing${stylePath}list.md`;
	return {
		referencing_style: hasReferencingStyle ? `referencing${stylePath}styles.md` : referencingList,
		referencing_overview: hasReferencingStyle ? `referencing${stylePath}overview.md` : referencingList,
		academic_integrity: "academic_integrity/integrity_url.md",
		generative_ai: "generative_ai/generative_ai_and_academic_integrity.md",
		quoting: `referencing${stylePath}quoting.md`,
		paraphrasing: "referencing/paraphrasing.md",
		declaration: "using_cadmus/submit_dec.md",
		start: "using_cadmus/getting_started.md"
	};
}
/** custom hook that returns a callback to open preset pages in Manual */
function useManualPreset() {
	const { articles, presets, setOpen, changePage } = useContext(ManualContext);
	return useCallback((preset) => {
		const presetSlug = presets[preset];
		const selectedArticle = presetSlug != null ? articles.find((a) => a.slug === presetSlug) : void 0;
		if (selectedArticle != null) {
			if (selectedArticle.url == null || selectedArticle.url === "") setOpen(true);
			changePage({
				pageType: "article",
				selectedArticle
			});
		}
	}, [
		articles,
		setOpen,
		changePage,
		presets
	]);
}
//#endregion
//#region src/useManualOpen.tsx
/** custom hook that returns the open state of Manual */
function useManualOpen() {
	const { isOpen } = useContext(ManualContext);
	return useMemo(() => isOpen, [isOpen]);
}
//#endregion
//#region src/manual-modal/triggers/CircularTrigger.tsx
/**
* A circular button with an icon to open the manual.
*/
const CircularTrigger = forwardRef((_, ref) => {
	const { setOpen, changePage } = useContext(ManualContext);
	return /* @__PURE__ */ jsx(ManualIconButton, {
		shape: "circle",
		ref,
		onClick: () => {
			setOpen(true);
			changePage({ pageType: "home" });
		},
		"aria-label": "Cadmus Manual",
		tooltipPosition: "left",
		children: /* @__PURE__ */ jsx(Icon, { iconName: "Book" })
	});
});
const ManualIconButton = styled(IconButton)`
  background: white;
  box-shadow: ${levels.three};
`;
//#endregion
//#region src/manual-modal/triggers/index.ts
const ManualTriggers = { CircularTrigger };
//#endregion
//#region src/components/helper/Header.tsx
const Container$3 = styled.div`
  width: 100%;
  padding: 0px 24px 8px 24px;
  box-sizing: border-box;
  justify-self: flex-start;
  display: flex;
  flex-direction: column;
  flex: none;
`;
const TitleActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  margin-bottom: 8px;
`;
const Title = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 4px;
  gap: 4px;
  flex: auto;
`;
/** Header components to indicate which page the user is on */
const Header = ({ title, subtitle, onClose, goBack }) => {
	return /* @__PURE__ */ jsxs(Container$3, { children: [/* @__PURE__ */ jsxs(TitleActions, { children: [/* @__PURE__ */ jsx("div", { children: goBack && /* @__PURE__ */ jsx(Button, {
		kind: "ghost",
		size: "sm",
		onClick: goBack,
		"aria-label": "go back",
		icon: /* @__PURE__ */ jsx(ArrowLeftIcon, {
			size: 20,
			label: ""
		}),
		children: "Back"
	}) }), /* @__PURE__ */ jsx(RadixDialog.DialogClose, {
		asChild: true,
		children: /* @__PURE__ */ jsx(ControlButton, { icon: /* @__PURE__ */ jsx(CrossIcon, {
			size: 16,
			label: "close manual",
			onClick: onClose
		}) })
	})] }), /* @__PURE__ */ jsxs(Title, { children: [/* @__PURE__ */ jsx(Text, {
		kind: "headingFour",
		style: {
			flex: "auto",
			overflow: "hidden",
			textOverflow: "ellipsis"
		},
		children: title
	}), /* @__PURE__ */ jsx(Text, {
		kind: "label",
		as: "span",
		children: subtitle
	})] })] });
};
//#endregion
//#region src/components/helper/Help.tsx
const Container$2 = styled.div`
  height: 56px;
  padding: 9px 27px;
  border-top: 1px solid ${(p) => p.theme.divider};
  box-sizing: border-box;
  flex: none;

  justify-self: flex-end;
  display: flex;
  justify-content: space-between;
  align-items: center;

  background-color: ${(p) => p.theme.colors.orange100};
`;
/** Help block for students to send a message for help */
const Help = ({ onAskHelp }) => /* @__PURE__ */ jsxs(Container$2, { children: [/* @__PURE__ */ jsx(Text, {
	kind: "bodyMd",
	children: "Need help?"
}), /* @__PURE__ */ jsx(Button, {
	kind: "secondary",
	onClick: onAskHelp,
	children: "Contact Support"
})] });
//#endregion
//#region src/components/helper/SearchBox.tsx
const Search = styled.input`
  width: 100%;
  height: 54px;
  padding: 0px 27px;

  border: none;
  outline: none;

  font-size: 14px;
  color: ${(p) => p.theme.text.main};
  background: ${(p) => p.theme.colors.orange100};

  &::placeholder {
    color: ${(p) => p.theme.text.shade1};
  }

  &:focus-visible {
    ${(p) => p.theme.focusInset};
  }
`;
/** SearchBox used to input query for searching articles
* search is debounced at 500ms
*/
const SearchBox = ({ query, makeQuery }) => {
	const [internalQuery, setInternalQuery] = useDebouncedDispatch(query, makeQuery, 400);
	const handleChange = (event) => {
		const newQuery = event.target.value;
		setInternalQuery(newQuery);
	};
	return /* @__PURE__ */ jsx(Search, {
		type: "search",
		value: internalQuery,
		onChange: handleChange,
		placeholder: "Search help docs or university resources",
		"aria-label": "Search help docs or university resources"
	});
};
/**
* Debounce a `dispatch` function call on a value which can be changed
* independently.
*
* Returns a normal `useState` return tuple which can be used transparently. The
* 'dispatch' callback will be called debounced by `debounceMs`.
*
*/
function useDebouncedDispatch(value, dispatch, debounceMs = 400) {
	const [internalValue, setInternalValue] = useState(value);
	useEffect(() => setInternalValue(value), [value, setInternalValue]);
	useEffect(() => {
		const timer = setTimeout(() => {
			if (value !== internalValue) dispatch(internalValue);
		}, debounceMs);
		return () => clearTimeout(timer);
	}, [
		internalValue,
		value,
		dispatch,
		debounceMs
	]);
	return [internalValue, setInternalValue];
}
styled.div`
  position: absolute;
  right: 0px;
  height: 100%;
  width: 360px;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.colors.orange100};
  border-left: 1px solid ${(p) => p.theme.divider};
  z-index: 1001;

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;
/**
* A styled Radix Dialog Content element.
*/
const TrayContent = styled(RadixDialog.Content)`
  position: absolute;
  top: 0px;
  right: 0px;
  bottom: 0px;
  width: 360px;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.colors.orange100};
  border-left: 1px solid ${(p) => p.theme.divider};
  z-index: 1001;

  &:focus-visible {
    outline: none; // remove browser default outline
    &::before {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      outline: none;
      ${(p) => p.theme.focusInset}
    }
  }

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;
const Scrollable = styled.div`
  flex-grow: 2;
  display: flex;
  flex-direction: column;

  overflow: auto;
  /* Remove scrollbar space */
  ::-webkit-scrollbar {
    width: 0px;
  }
`;
//#endregion
//#region src/manual-modal/modal/CurrentHeader.tsx
/**
* Renders the correct header based on the current page.
*/
const CurrentHeader = () => {
	const { page, makeQuery, setOpen, goBack } = useContext(ManualContext);
	const onClose = () => {
		makeQuery("");
		setOpen(false);
	};
	switch (page.pageType) {
		case "home": return /* @__PURE__ */ jsx(Header, {
			title: "Cadmus Manual",
			subtitle: "Home",
			onClose
		});
		case "index": return /* @__PURE__ */ jsx(Header, {
			title: page.categoryName,
			subtitle: "Index",
			onClose,
			goBack
		});
		case "article": return /* @__PURE__ */ jsx(Header, {
			title: page.selectedArticle.title,
			subtitle: page.selectedArticle.category,
			onClose,
			goBack
		});
		case "result": return /* @__PURE__ */ jsx(Header, {
			title: "Search Results",
			subtitle: "Search",
			onClose,
			goBack: () => {
				makeQuery("");
			}
		});
	}
};
//#endregion
//#region src/components/pages/ArticleContent.tsx
const Paper = styled.div`
  font-family:
    "Open Sauce Two",
    ui-sans-serif,
    -apple-system;
  color: ${(p) => p.theme.colors.black100};

  p {
    font-weight: 400;
    font-size: 1em;
    margin-bottom: 1em;
    padding: 0 24px;
    line-height: 180%;
    letter-spacing: 0px;
    color: ${(p) => p.theme.colors.black100};
  }

  li > p {
    padding: 0px;
  }

  ul,
  ol {
    font-size: 15px;
    margin-bottom: 1em;
    line-height: 24px;
    letter-spacing: 0.2px;
    padding-left: 32px;
    padding-right: 24px;
    color: ${(p) => p.theme.colors.black100};
  }

  video,
  img {
    width: 100%;
  }

  table {
    width: 100%;
    margin-top: 1em;
    margin-bottom: 1em;
    padding: 0 24px;
    text-align: left;
  }

  td {
    padding: 6px 6px;
  }

  code {
    font-size: 0.875em;
    border: 1px solid ${(p) => p.theme.divider};
    background: ${(p) => p.theme.background.action2};
    border-radius: 3px;
    padding: 6px;
  }

  blockquote {
    font-size: inherit;
    border-left: 1px solid ${(p) => p.theme.divider};
    border-top: 1px solid transparent;
    border-bottom: 1px solid transparent;

    margin: 1em 0em;
    margin-left: 24px;
    padding: 0;
  }

  blockquote > p {
    color: ${(p) => p.theme.text.shade1};
  }

  li + li {
    margin-top: 0.25em;
  }

  h3 {
    font-weight: 600;
    color: ${(p) => p.theme.text.heading};
    font-size: 18px;
    margin-top: 3em;
    margin-bottom: 1em;
    padding: 0 24px;
    line-height: 24px;
    letter-spacing: 0.2px;
  }

  h4 {
    font-weight: 600;
    color: ${(p) => p.theme.text.heading};
    font-size: 1.25rem;
    margin-top: 3em;
    margin-bottom: 1em;
    padding: 0 24px;
    line-height: 120%;
    letter-spacing: 0px;
  }

  ol {
    list-style-position: outside;
    margin-left: 1em;
    margin-top: 1em;
  }

  ul {
    list-style: disc outside;
    margin-left: 1em;
    margin-top: 1em;
  }

  ul ul {
    list-style-type: circle;
    margin-top: 0.5em;
  }

  ul ul ul {
    list-style-type: square;
  }

  dd {
    margin-left: 1em;
  }

  figure {
    margin-left: 1em;
    margin-right: 1em;
    text-align: center;
  }

  figure:not(:first-child) {
    margin-top: 2em;
  }

  figure:not(:last-child) {
    margin-bottom: 2em;
  }

  figure img {
    display: inline-block;
  }

  figure figcaption {
    font-style: italic;
  }

  pre {
    -webkit-overflow-scrolling: touch;
    overflow-x: auto;
    padding: 1.25em 1.5em;
    white-space: pre;
    word-wrap: normal;
  }

  sup,
  sub {
    font-size: 75%;
  }

  a {
    color: ${(p) => p.theme.text.accent};
  }

  /* for keyboard shortcut related articles */
  #mac-toggle,
  #win-toggle,
  .mac-shortcuts,
  .win-shortcuts {
    display: none;
  }

  #mac-label,
  #win-label {
    cursor: pointer;

    &:hover {
      color: ${(p) => p.theme.background.action3};
    }
  }

  #mac-toggle:checked ~ .mac-shortcuts,
  #win-toggle:checked ~ .win-shortcuts {
    display: table;
  }

  #mac-toggle:checked ~ h4 > #mac-label,
  #win-toggle:checked ~ h4 > #win-label {
    color: ${(p) => p.theme.text.accent};
  }
`;
const ArticleContent = ({ content, openLink }) => {
	const innerHtml = { __html: content };
	/**
	* Capture internal clicks and contain them if they are
	* intra-manual links. i.e. links with href starting with `manual://`
	*/
	const bodyEl = React.useRef(null);
	const handleInternalClicks = (event) => {
		const target = event.target;
		if (bodyEl.current && target.href && bodyEl.current.contains(target)) {
			const { href } = target;
			if (href && typeof href === "string" && href.startsWith("manual://")) {
				event.preventDefault();
				openLink(href.slice(9));
			}
		}
	};
	return /* @__PURE__ */ jsx("div", {
		ref: bodyEl,
		onClick: handleInternalClicks,
		children: /* @__PURE__ */ jsx(Paper, { dangerouslySetInnerHTML: innerHtml })
	});
};
//#endregion
//#region src/components/pages/ArticlePage.tsx
const Container$1 = styled.div`
  flex-grow: 2;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const VoteBlock = styled.div`
  justify-self: flex-end;
  display: flex;
  flex-direction: column;

  padding: 0 27px;

  background: ${(p) => p.theme.colors.orange100};
`;
const VoteIconContainer = styled.div`
  height: 45px;
  padding: 0 45px;

  display: flex;
  justify-content: space-around;
  align-items: center;
`;
const ArticlePage = ({ selectedArticle, onVote, changePage, articles, referencingStyle }) => {
	/**
	* open another Manual article using its `slug`.
	* used in ArticleContent when clicking intra-manual links.
	*/
	const openLink = (slug) => {
		const replacedSlug = slug.replace("${REFERENCING_STYLE}", referencingStyle);
		const selectedArticle = articles.find((a) => a.slug === replacedSlug);
		if (selectedArticle) changePage({
			pageType: "article",
			selectedArticle
		});
	};
	return /* @__PURE__ */ jsxs(Container$1, { children: [
		/* @__PURE__ */ jsx(ArticleContent, {
			content: selectedArticle.content,
			openLink
		}),
		/* @__PURE__ */ jsx(Spacer, { spacing: 108 }),
		/* @__PURE__ */ jsxs(VoteBlock, { children: [
			/* @__PURE__ */ jsx(Spacer, { spacing: 27 }),
			/* @__PURE__ */ jsx(Text, {
				kind: "headingSix",
				textAlign: "center",
				children: "Was this article helpful?"
			}),
			/* @__PURE__ */ jsx(Spacer, { spacing: 27 }),
			/* @__PURE__ */ jsxs(VoteIconContainer, { children: [/* @__PURE__ */ jsx(IconButton, {
				"aria-label": "upvote",
				selected: selectedArticle.vote === "Upvote",
				onClick: () => {
					onVote("Upvote", selectedArticle);
					changePage({
						pageType: "article",
						selectedArticle: {
							...selectedArticle,
							vote: "Upvote"
						}
					});
				},
				children: /* @__PURE__ */ jsx(Icon$1, { iconName: "ThumbsUp" })
			}), /* @__PURE__ */ jsx(IconButton, {
				"aria-label": "downvote",
				selected: selectedArticle.vote === "Downvote",
				onClick: () => {
					onVote("Downvote", selectedArticle);
					changePage({
						pageType: "article",
						selectedArticle: {
							...selectedArticle,
							vote: "Downvote"
						}
					});
				},
				children: /* @__PURE__ */ jsx(Icon$1, { iconName: "ThumbsDown" })
			})] }),
			/* @__PURE__ */ jsx(Spacer, { spacing: 36 })
		] })
	] });
};
//#endregion
//#region src/components/pages/ArticleList.tsx
const ArticleLinkContainer = styled.button`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 40px;
  text-align: left;
  padding: 9px 27px;
  font-size: 15px;
  flex: none;

  border: none;
  background: transparent;
  color: ${(p) => p.theme.text.main};
  fill: ${(p) => p.theme.text.main};

  text-decoration: none;

  transition:
    background 0.3s ${easeOutCirc},
    fill 0.3s ${easeOutCirc};

  outline: none;
  cursor: pointer;

  &:not(:disabled) {
    &:hover,
    &:focus {
      background: ${(p) => p.theme.background.action1};
    }

    &:active {
      background: ${(p) => p.theme.background.action2};
    }
  }

  &:disabled {
    opacity: 0.36;
    cursor: not-allowed;
    text-decoration: line-through;
  }
`;
const ShowMore = styled(Text)`
  font-style: italic;
`;
/** ArticleList - displays a list of <ArticleLink />.
* if capped up to 3 <ArticleLink /> will be displayed
* if there are more than 3 articles a 'Show more' button will be shown
*/
const ArticleList = ({ title, titleColor, articles, onArticleLinkClick, onShowMore }) => {
	let displayArticles;
	if (onShowMore) displayArticles = articles.slice(0, 3);
	else displayArticles = articles;
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(Text, {
			kind: "headingSix",
			color: "shade1",
			style: {
				padding: "0px 27px",
				margin: "9px 0px"
			},
			children: titleColor ? /* @__PURE__ */ jsx(Colored, {
				color: titleColor,
				children: title
			}) : title
		}),
		/* @__PURE__ */ jsx(Spacer, { spacing: 18 }),
		displayArticles.map((article) => /* @__PURE__ */ jsx(ArticleLinkContainer, {
			onClick: () => onArticleLinkClick(article),
			children: /* @__PURE__ */ jsxs(Text, {
				kind: "bodyMd",
				color: "main",
				children: [article.title, article.url != null && article.url !== "" && /* @__PURE__ */ jsx(StyledOpen, {})]
			})
		}, article.id)),
		onShowMore && /* @__PURE__ */ jsx(ArticleLinkContainer, {
			onClick: onShowMore,
			children: /* @__PURE__ */ jsx(ShowMore, {
				kind: "bodyLine",
				color: "shade1",
				children: "Show more"
			})
		})
	] });
};
const StyledOpen = styled(Icon$1).attrs({ iconName: "Open" })`
  margin-left: 9px;
  opacity: 0.36;
`;
//#endregion
//#region src/components/pages/CategoryPage.tsx
/**
* Category page
* Displays all articles related to the category.
* articles are grouped into sections
*/
function CategoryPage(props) {
	const { articles, categoryName, changePage } = props;
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(Spacer, { spacing: 18 }),
		/* @__PURE__ */ jsx(ArticleList, {
			title: categoryName,
			articles: articles.filter((a) => a.category === categoryName),
			onArticleLinkClick: (article) => changePage({
				pageType: "article",
				selectedArticle: article
			})
		}),
		/* @__PURE__ */ jsx(Spacer, { spacing: 108 })
	] });
}
//#endregion
//#region src/components/pages/HomePage.tsx
/**
* Manual Home page with all the top level Category links and external help
* docs.
*/
const HomePage = ({ articles, changePage }) => {
	const items = React.useMemo(() => {
		return Array.from(new Set(articles.map((a) => a.category))).sort().map((category) => ({
			category,
			articles: articles.filter((a) => a.category === category)
		}));
	}, [articles]);
	const a11yStmtItem = articles.find((a) => a.slug === "accessibility_statement.md");
	if (articles.length === 0) return /* @__PURE__ */ jsx(ArticleContent, {
		openLink: () => null,
		content: "<p>Sorry! Something went wrong, we could not load any articles. You may be experiencing internet connection issues. Please check your connection and try again.</p>"
	});
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(Introduction, {
			kind: "systemMd",
			italic: true,
			children: "Our skills guides are here to help you through every stage of your assessment. Check them out and submit your best work."
		}),
		/* @__PURE__ */ jsx(Divider, {}),
		/* @__PURE__ */ jsx(Spacer, { spacing: 18 }),
		a11yStmtItem && /* @__PURE__ */ jsx(ArticleLinkContainer, {
			onClick: () => {
				changePage({
					pageType: "article",
					selectedArticle: a11yStmtItem
				});
			},
			children: /* @__PURE__ */ jsx(Text, {
				kind: "headingSix",
				children: a11yStmtItem.title
			})
		}, a11yStmtItem.slug),
		items.map((item) => /* @__PURE__ */ jsx(ArticleLinkContainer, {
			onClick: () => {
				changePage({
					pageType: "index",
					categoryName: item.category
				});
			},
			children: /* @__PURE__ */ jsx(Text, {
				kind: "headingSix",
				children: item.category
			})
		}, item.category)),
		/* @__PURE__ */ jsx(Spacer, { spacing: 27 }),
		/* @__PURE__ */ jsx(ArticleLinkContainer, {
			as: "a",
			href: "https://support.cadmus.io/students",
			target: "_blank",
			children: /* @__PURE__ */ jsxs(Text, {
				kind: "bodyMd",
				color: "shade1",
				bold: true,
				children: ["Cadmus Help Docs", /* @__PURE__ */ jsx(StyledOpen, {})]
			})
		}),
		/* @__PURE__ */ jsx(Spacer, { spacing: 108 })
	] });
};
const Introduction = styled(Text)`
  padding: 9px 27px;
  margin: 9px 0px;
  color: hsl(245, 90%, 65%);
`;
//#endregion
//#region src/components/pages/HSArticleList.tsx
/** List search article results from Hubspot. */
const HSArticleList = (props) => {
	const { articles } = props;
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(Text, {
			kind: "headingSix",
			color: "shade1",
			style: {
				padding: "0px 27px",
				margin: "9px 0px"
			},
			children: /* @__PURE__ */ jsx(Colored, {
				color: "purple800",
				children: "Using Cadmus"
			})
		}),
		/* @__PURE__ */ jsx(Spacer, { spacing: 18 }),
		articles.map((article) => /* @__PURE__ */ jsx(Container, {
			onClick: () => {
				const url = article.url.includes("support.cadmus.io") ? article.url : `https://${article.domain}${article.url}`;
				window.open(url, "_blank");
			},
			children: /* @__PURE__ */ jsxs(Text, {
				kind: "bodyMd",
				color: "main",
				children: [/* @__PURE__ */ jsx("span", { dangerouslySetInnerHTML: { __html: xss(article.title, { whiteList: { span: ["class"] } }) } }), /* @__PURE__ */ jsx(StyledOpen, {})]
			})
		}, article.id))
	] });
};
const Container = styled(ArticleLinkContainer)`
  .hs-search-highlight {
    background-color: ${colors.yellowA500};
  }
`;
//#endregion
//#region src/components/pages/ResultsPage.tsx
/**
* Search results page, displays articles from Cadmus manual search and Hubsport knowledge base.
*/
function ResultsPage(props) {
	const { results, changePage, hsSearchResults } = props;
	const length = results.length + hsSearchResults.length;
	const title = `${length} article${length === 1 ? "" : "s"} found`;
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(Text, {
			kind: "headingSix",
			color: "shade1",
			style: {
				padding: "0px 27px",
				margin: "9px 0px"
			},
			children: title
		}),
		results.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(ArticleList, {
			title: "University Resources",
			titleColor: "orange500",
			articles: results,
			onArticleLinkClick: (article) => changePage({
				pageType: "article",
				selectedArticle: article
			})
		}), /* @__PURE__ */ jsx(Spacer, { spacing: 27 })] }),
		hsSearchResults.length > 0 && /* @__PURE__ */ jsx(HSArticleList, { articles: hsSearchResults }),
		/* @__PURE__ */ jsx(Spacer, { spacing: 27 })
	] });
}
//#endregion
//#region src/manual-modal/modal/CurrentPage.tsx
/**
* Renders the correct page based on the manual state.
*/
const CurrentPage = () => {
	const { articles, changePage, page, referencingStyle, onVote, hsSearchResults, results } = useContext(ManualContext);
	switch (page.pageType) {
		case "home": return /* @__PURE__ */ jsx(HomePage, {
			articles,
			changePage
		});
		case "index": return /* @__PURE__ */ jsx(CategoryPage, {
			articles,
			changePage,
			categoryName: page.categoryName
		});
		case "article": return /* @__PURE__ */ jsx(ArticlePage, {
			articles,
			changePage,
			referencingStyle,
			onVote,
			selectedArticle: page.selectedArticle
		});
		case "result": return /* @__PURE__ */ jsx(ResultsPage, {
			changePage,
			hsSearchResults,
			results
		});
	}
};
//#endregion
//#region src/manual-modal/modal/ManualModal.tsx
function ManualModal(props) {
	const { mountElement, trigger, onAskHelp } = props;
	const { isOpen, setOpen, page, query, makeQuery } = useContext(ManualContext);
	/** Scroll to top when pageType changes. */
	const scrollbarRef = useRef(null);
	useEffect(() => {
		const scrollbar = scrollbarRef.current;
		if (scrollbar) scrollbar.scrollTo({ top: 0 });
	}, [page.pageType, scrollbarRef]);
	return /* @__PURE__ */ jsxs(RadixDialog.Root, {
		open: isOpen,
		onOpenChange: setOpen,
		children: [trigger && /* @__PURE__ */ jsx(RadixDialog.Trigger, {
			asChild: true,
			children: trigger
		}), /* @__PURE__ */ jsx(RadixDialog.Portal, {
			container: mountElement.current,
			children: /* @__PURE__ */ jsxs(TrayContent, {
				onEscapeKeyDown: () => setOpen(false),
				children: [/* @__PURE__ */ jsxs(Scrollable, {
					ref: scrollbarRef,
					children: [/* @__PURE__ */ jsxs("div", { children: [
						/* @__PURE__ */ jsx(CurrentHeader, {}),
						/* @__PURE__ */ jsx(Divider, {}),
						page.pageType !== "article" && /* @__PURE__ */ jsx(SearchBox, {
							query,
							makeQuery
						}),
						/* @__PURE__ */ jsx(Divider, {})
					] }), /* @__PURE__ */ jsx(CurrentPage, {})]
				}), onAskHelp && /* @__PURE__ */ jsx(Help, { onAskHelp })]
			})
		})]
	});
}
//#endregion
export { ManualContext, ManualModal, ManualProvider, ManualTriggers, manualPresets, useManualOpen, useManualPreset };

//# sourceMappingURL=index.mjs.map